import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpDown,
  Columns3,
  ExternalLink,
  FileText,
  Info,
  LayoutList,
  Loader2,
  MailCheck,
  Map as MapIcon,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  Star,
  Trash2,
  Upload,
  UserCheck,
  UserPlus,
  X,
  Camera,
  Plane,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ApertureLoader from "@/components/ApertureLoader";
import ApplicantHeatmap from "@/components/applicants/ApplicantHeatmap";
import ApplicantKanban from "@/components/applicants/ApplicantKanban";
import { api } from "@/lib/api";

/**
 * Applicant Pipeline — fully native (MongoDB-backed) admin CRUD.
 *
 * Applicants are internal-only records. They are NOT portal users; the
 * "Invite to Portal" button is what promotes an applicant into a real
 * photographer (creates the User + fires the magic link). Everything on
 * this page is admin-restricted at the route layer.
 */

// Safe-URL guard — reject anything that isn't http(s). Mirrors the server-side
// validator so even if a legacy applicant row has a `javascript:` resume URL,
// clicking it in the admin UI does nothing.
const safeHref = (u) => {
  if (!u || typeof u !== "string") return null;
  const s = u.trim();
  const low = s.toLowerCase();
  if (low.startsWith("http://") || low.startsWith("https://")) return s;
  return null;
};

const STAGE_OPTIONS = [
  "Not Contacted",
  "Contacted",
  "In Review",
  "Interview Scheduled",
  "Accepted",
  "Rejected",
];

// Pill palette per stage. Values that don't match fall back to a neutral chip.
const STAGE_STYLE = {
  "Accepted":            "bg-[#2E7D32]/12 text-[#1B5E20] border-[#2E7D32]/25",
  "Interview Scheduled": "bg-[#1976D2]/12 text-[#0D47A1] border-[#1976D2]/25",
  "Contacted":           "bg-[#F9A825]/15 text-[#B08D57] border-[#F9A825]/30",
  "In Review":           "bg-[#E65100]/12 text-[#BF360C] border-[#E65100]/25",
  "Rejected":            "bg-[#78584B]/12 text-[#4D3F35] border-[#78584B]/30",
  "Not Contacted":       "bg-[#1A0F0A]/6 text-[#4D3F35] border-[#1A0F0A]/15",
};

const emptyForm = () => ({
  name: "",
  email: "",
  phone: "",
  zip_code: "",
  region: "North Carolina",
  stage: "Not Contacted",
  desired_location: "",
  desired_travel_distance: "",
  notes: "",
  score: 0,
  resume_url: "",
});

// Sort options exposed in the toolbar. Kept as an ordered list so the Select
// renders in the exact order below.
const SORT_OPTIONS = [
  { value: "recent",     label: "Newest first" },
  { value: "oldest",     label: "Oldest first" },
  { value: "score-desc", label: "Score · high → low" },
  { value: "score-asc",  label: "Score · low → high" },
  { value: "stage",      label: "Stage · pipeline order" },
  { value: "name-asc",   label: "Name · A → Z" },
  { value: "name-desc",  label: "Name · Z → A" },
  { value: "location",   label: "Location · A → Z" },
  { value: "region",     label: "Region · A → Z" },
  { value: "zip",        label: "ZIP · ascending" },
];
// Stage → numeric rank so "Stage · pipeline order" is meaningful. Puts fresh
// leads at the top and Rejected at the bottom.
const STAGE_RANK = {
  "Not Contacted": 0,
  "Contacted": 1,
  "In Review": 2,
  "Interview Scheduled": 3,
  "Accepted": 4,
  "Rejected": 5,
};

// String comparator that pushes empty / null values to the bottom regardless
// of sort direction so blanks don't cluster at the top and drown out real
// data. Reused by name / location / region / zip sorts.
const cmpStr = (a, b) => {
  const ax = (a || "").trim().toLowerCase();
  const bx = (b || "").trim().toLowerCase();
  if (!ax && !bx) return 0;
  if (!ax) return 1;
  if (!bx) return -1;
  return ax.localeCompare(bx);
};

// Small inline role pill so admins can differentiate Photographer / Pilot
// leads at a glance. Legacy applicants that predate the `applied_role`
// field fall through to Photographer (matches backend default).
const ROLE_META = {
  photographer: {
    label: "Photographer",
    short: "PHOTOG",
    icon: Camera,
    classes: "border-[#6B0F1A]/30 bg-[#6B0F1A]/8 text-[#6B0F1A]",
  },
  pilot: {
    label: "Pilot",
    short: "PILOT",
    icon: Plane,
    classes: "border-[#3F7A2E]/40 bg-[#3F7A2E]/10 text-[#3F7A2E]",
  },
};

function RoleBadge({ role, size = "sm", testId }) {
  const meta = ROLE_META[role] || ROLE_META.photographer;
  const Icon = meta.icon;
  const sizing =
    size === "lg"
      ? "text-[10px] px-2 py-0.5 gap-1.5"
      : "text-[9px] px-1.5 py-0.5 gap-1";
  const iconSize = size === "lg" ? "w-3 h-3" : "w-2.5 h-2.5";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded-sm border font-mono-tag tracking-[0.16em] ${sizing} ${meta.classes}`}
      title={meta.label}
    >
      <Icon className={iconSize} strokeWidth={2} />
      {meta.short}
    </span>
  );
}

export default function ApplicantsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  // Role filter — All / Photographer / Pilot. Applies to both the pipeline
  // table and the heatmap (via the same `filtered` list piped downstream).
  // Employees never appear here — /apply only accepts photographer + pilot.
  const [roleTab, setRoleTab] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  // null when closed; { mode: "create" | "edit", value: applicantOrDraft }
  const [editorOpen, setEditorOpen] = useState(null);
  // "pipeline" (default table view) | "heatmap" (interactive map dashboard).
  const [view, setView] = useState("pipeline");
  // Set<stage> — which stages contribute to the heatmap. Everything on by
  // default so the first render shows the full applicant footprint.
  const [heatmapStages, setHeatmapStages] = useState(() => new Set(STAGE_OPTIONS));
  // CSV import state — the modal + the pending region for the upload.
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvRegion, setCsvRegion] = useState("North Carolina");
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvInputRef = useRef(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await api.get("/admin/applicants");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load applicants.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!data?.applicants) return [];
    const q = query.trim().toLowerCase();
    const passFilter = (a) => {
      if (activeTab !== "All" && a.region !== activeTab) return false;
      if (roleTab !== "all" && (a.applied_role || "photographer") !== roleTab) return false;
      if (!q) return true;
      return (
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.desired_location || "").toLowerCase().includes(q) ||
        (a.zip_code || "").toLowerCase().includes(q)
      );
    };
    const rows = data.applicants.filter(passFilter);
    // Sort — every comparator returns a stable number so sort is stable
    // enough for our use. `recent` is the default (server already returns
    // newest first, so no re-sort needed).
    const sorters = {
      recent:     (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest:     (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      "score-desc": (a, b) => (b.score || 0) - (a.score || 0)
                             || (b.created_at || "").localeCompare(a.created_at || ""),
      "score-asc":  (a, b) => (a.score || 0) - (b.score || 0)
                             || (b.created_at || "").localeCompare(a.created_at || ""),
      stage:      (a, b) => (STAGE_RANK[a.stage] ?? 99) - (STAGE_RANK[b.stage] ?? 99)
                          || cmpStr(a.name, b.name),
      "name-asc":  (a, b) => cmpStr(a.name, b.name),
      "name-desc": (a, b) => cmpStr(b.name, a.name),
      location:    (a, b) => cmpStr(a.resolved_city || a.desired_location, b.resolved_city || b.desired_location),
      region:      (a, b) => cmpStr(a.region, b.region) || cmpStr(a.name, b.name),
      zip:         (a, b) => cmpStr(a.zip_code, b.zip_code) || cmpStr(a.name, b.name),
    };
    const sorter = sorters[sortBy];
    return sorter ? [...rows].sort(sorter) : rows;
  }, [data, activeTab, roleTab, query, sortBy]);

  const perTabCounts = useMemo(() => {
    const map = { All: 0 };
    (data?.applicants || []).forEach((a) => {
      map.All += 1;
      map[a.region] = (map[a.region] || 0) + 1;
    });
    return map;
  }, [data]);

  // Live per-role counts for the applicant Role tab strip. Employees are
  // never accepted through /apply so the pool is always Photog + Pilot.
  // Legacy rows without `applied_role` roll into the Photographer bucket.
  const perRoleCounts = useMemo(() => {
    const c = { all: 0, photographer: 0, pilot: 0 };
    (data?.applicants || []).forEach((a) => {
      c.all += 1;
      const r = a.applied_role === "pilot" ? "pilot" : "photographer";
      c[r] += 1;
    });
    return c;
  }, [data]);

  // Applicant list pre-filtered by role only. Piped into Kanban + Heatmap
  // (they don't run the region/search/stage filter chain, so we can't reuse
  // `filtered`). Region + search filters still apply on top for the
  // Pipeline table view via `filtered`.
  const roleFilteredApplicants = useMemo(() => {
    const list = data?.applicants || [];
    if (roleTab === "all") return list;
    return list.filter((a) => (a.applied_role || "photographer") === roleTab);
  }, [data, roleTab]);

  // Optimistic patch: update local state instantly, then PATCH to backend. On
  // failure, revert. Keeps inline edits (stage, score, notes) feeling instant.
  const patchApplicant = async (applicant, patch) => {
    const prev = data;
    const optimistic = {
      ...data,
      applicants: data.applicants.map((a) =>
        a.id === applicant.id ? { ...a, ...patch } : a,
      ),
    };
    setData(optimistic);
    try {
      const { data: updated } = await api.patch(`/admin/applicants/${applicant.id}`, patch);
      setData((cur) => ({
        ...cur,
        applicants: cur.applicants.map((a) => (a.id === updated.id ? updated : a)),
      }));
    } catch (e) {
      setData(prev);
      toast.error(e?.response?.data?.detail || "Could not update applicant.");
    }
  };

  const removeApplicant = async (applicant) => {
    if (!window.confirm(`Delete ${applicant.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/applicants/${applicant.id}`);
      setData((cur) => ({
        ...cur,
        applicants: cur.applicants.filter((a) => a.id !== applicant.id),
      }));
      toast.success(`Deleted ${applicant.name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete applicant.");
    }
  };

  // Two-step transition: (1) admin clicks "Start Onboarding" → confirmation
  // dialog with a clear "here's what will happen" summary; (2) admin confirms
  // → backend creates the photographer user, fires magic-link email, and
  // stamps applicant.stage = "Invited". Applicants themselves never receive
  // ANY email until this button is clicked — that's the entire point.
  const [onboardConfirm, setOnboardConfirm] = useState(null); // applicant | null
  const [onboardBusy, setOnboardBusy] = useState(false);

  const openOnboardConfirm = (applicant) => {
    if (!applicant.email) {
      toast.error("Applicant has no email on file. Add one before onboarding.");
      return;
    }
    setOnboardConfirm(applicant);
  };

  const confirmOnboard = async () => {
    if (!onboardConfirm) return;
    setOnboardBusy(true);
    try {
      await api.post(`/admin/applicants/${onboardConfirm.id}/invite`);
      toast.success(
        `Onboarding started — magic link sent to ${onboardConfirm.email}.`,
        { description: "They'll now see a Reelty onboarding email in their inbox." },
      );
      setOnboardConfirm(null);
      load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start onboarding.");
    } finally {
      setOnboardBusy(false);
    }
  };

  const openCreate = () => setEditorOpen({ mode: "create", value: emptyForm() });
  const openEdit = (a) => setEditorOpen({ mode: "edit", value: { ...a } });

  const saveEditor = async (draft) => {
    if (!draft.name || !draft.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    try {
      if (editorOpen.mode === "create") {
        const { data: created } = await api.post("/admin/applicants", draft);
        setData((cur) => ({
          ...cur,
          applicants: [created, ...(cur?.applicants || [])],
        }));
        toast.success(`Added ${created.name}.`);
      } else {
        const { data: updated } = await api.patch(`/admin/applicants/${draft.id}`, draft);
        setData((cur) => ({
          ...cur,
          applicants: cur.applicants.map((a) => (a.id === updated.id ? updated : a)),
        }));
        toast.success("Applicant updated.");
      }
      setEditorOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  if (loading) return <ApertureLoader label="Loading applicants…" />;
  if (!data) return null;

  const tabs = ["All", ...(data.tabs || [])];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 md:py-10" data-testid="applicants-admin">
      {/* Header — stacks vertically below xl (1280px) so the description
          paragraph can't get crushed by the toolbar's natural width
          (Pipeline/Kanban/Heatmap + Refresh + Import CSV + New applicant,
          which totals ~750px). Only from xl+ does the layout go
          side-by-side, where the container is wide enough that the left
          column keeps ~500px+ for the description to render normally. */}
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4 mb-8">
        <div className="min-w-0 xl:flex-1">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-xs font-mono-tag text-[#78584B] hover:text-[#6B0F1A] mb-3"
            data-testid="back-to-roster"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={2} /> BACK TO ROSTER
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#1A0F0A]">
            Applicant Pipeline
          </h1>
          <p className="text-sm md:text-base text-[#4D3F35] mt-3 max-w-2xl leading-relaxed">
            Internal-only. Track prospective photographers across every region — capture their info,
            score them, jot notes, and promote the best ones to the portal with a single click.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {/* Pipeline / Kanban / Heatmap view toggle — one control, everything
              below swaps between the three ways to work the applicant list. */}
          <div
            className="inline-flex rounded-sm border border-[#1A0F0A]/15 overflow-hidden shrink-0"
            data-testid="applicants-view-toggle"
          >
            <button
              type="button"
              onClick={() => setView("pipeline")}
              data-testid="view-pipeline-btn"
              className={`px-3 h-9 inline-flex items-center gap-1.5 text-xs font-bold tracking-wide transition-colors ${
                view === "pipeline"
                  ? "bg-[#6B0F1A] text-[#F5EBD9]"
                  : "bg-transparent text-[#4D3F35] hover:bg-[#1A0F0A]/5"
              }`}
            >
              <LayoutList className="w-3.5 h-3.5" strokeWidth={2} />
              Pipeline
            </button>
            <button
              type="button"
              onClick={() => setView("kanban")}
              data-testid="view-kanban-btn"
              className={`px-3 h-9 inline-flex items-center gap-1.5 text-xs font-bold tracking-wide transition-colors border-l border-[#1A0F0A]/15 ${
                view === "kanban"
                  ? "bg-[#6B0F1A] text-[#F5EBD9]"
                  : "bg-transparent text-[#4D3F35] hover:bg-[#1A0F0A]/5"
              }`}
            >
              <Columns3 className="w-3.5 h-3.5" strokeWidth={2} />
              Kanban
            </button>
            <button
              type="button"
              onClick={() => setView("heatmap")}
              data-testid="view-heatmap-btn"
              className={`px-3 h-9 inline-flex items-center gap-1.5 text-xs font-bold tracking-wide transition-colors border-l border-[#1A0F0A]/15 ${
                view === "heatmap"
                  ? "bg-[#6B0F1A] text-[#F5EBD9]"
                  : "bg-transparent text-[#4D3F35] hover:bg-[#1A0F0A]/5"
              }`}
            >
              <MapIcon className="w-3.5 h-3.5" strokeWidth={2} />
              Heatmap
            </button>
          </div>
          <Button
            onClick={() => load({ silent: true })}
            variant="ghost"
            className="border border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5 text-[#1A0F0A] font-bold tracking-wide rounded-sm flex-1 sm:flex-none"
            data-testid="refresh-applicants-btn"
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" strokeWidth={2} />
            )}
            Refresh
          </Button>
          <Button
            onClick={() => setCsvImportOpen(true)}
            data-testid="import-csv-btn"
            variant="ghost"
            className="border border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5 text-[#1A0F0A] font-bold tracking-wide rounded-sm flex-1 sm:flex-none"
          >
            <Upload className="w-4 h-4 mr-2" strokeWidth={2} />
            Import CSV
          </Button>
          <Button
            onClick={openCreate}
            data-testid="new-applicant-btn"
            className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm btn-primary-glow flex-1 sm:flex-none"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            New applicant
          </Button>
        </div>
      </div>

      {/* Role tabs — All / Photographer / Pilot. Sits above the view
          switch so the role slice applies to Pipeline, Kanban, AND
          Heatmap views uniformly. Filter cascades to the region tabs
          on Pipeline too. */}
      <div
        className="mb-3 -mx-4 sm:mx-0"
        role="tablist"
        aria-label="Filter applicants by role"
        data-testid="applicants-role-tabs"
      >
        <div className="flex items-center gap-2 px-4 sm:px-0 overflow-x-auto flex-nowrap scrollbar-none pb-1">
          <span className="font-mono-tag text-[10px] text-[#78584B] mr-2 shrink-0">ROLE</span>
          {[
            { key: "all",          label: "All",          icon: null },
            { key: "photographer", label: "Photographer", icon: Camera },
            { key: "pilot",        label: "Pilot",        icon: Plane },
          ].map((r) => {
            const isActive = roleTab === r.key;
            const Icon = r.icon;
            return (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setRoleTab(r.key)}
                data-testid={`applicants-role-tab-${r.key}`}
                className={`inline-flex items-center gap-1.5 font-mono-tag text-[10px] px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap shrink-0 ${
                  isActive
                    ? "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A]"
                    : "bg-transparent text-[#78584B] border-[#1A0F0A]/12 hover:text-[#6B0F1A] hover:bg-[#1A0F0A]/5"
                }`}
              >
                {Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
                {r.label.toUpperCase()}
                <span
                  className={`ml-0.5 font-mono-tag text-[9px] ${
                    isActive ? "text-[#F5EBD9]/70" : "text-[#78584B]/70"
                  }`}
                >
                  {perRoleCounts[r.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {view === "kanban" ? (
        <ApplicantKanban
          applicants={roleFilteredApplicants}
          regions={data.tabs || []}
          onPatch={patchApplicant}
          onInvite={openOnboardConfirm}
          onEdit={openEdit}
        />
      ) : view === "heatmap" ? (
        <>
          {/* HEATMAP DASHBOARD — stage filter chips on top, live map below. */}
          <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="heatmap-stage-filter">
            <span className="font-mono-tag text-[10px] text-[#78584B] mr-1">FILTER BY STAGE</span>
            {STAGE_OPTIONS.map((stage) => {
              const active = heatmapStages.has(stage);
              return (
                <button
                  key={stage}
                  type="button"
                  data-testid={`heatmap-stage-${stage.replace(/ /g, "-").toLowerCase()}`}
                  onClick={() => {
                    setHeatmapStages((cur) => {
                      const next = new Set(cur);
                      if (next.has(stage)) next.delete(stage);
                      else next.add(stage);
                      return next;
                    });
                  }}
                  className={`px-3 py-1.5 rounded-sm border text-xs font-bold tracking-wide transition-colors ${
                    active
                      ? (STAGE_STYLE[stage] || "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A]")
                      : "bg-transparent border-[#1A0F0A]/15 text-[#78584B]/60 hover:text-[#4D3F35]"
                  }`}
                >
                  {stage}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setHeatmapStages(new Set(STAGE_OPTIONS))}
              className="ml-2 font-mono-tag text-[10px] text-[#6B0F1A] hover:underline"
              data-testid="heatmap-select-all"
            >
              ALL
            </button>
            <button
              type="button"
              onClick={() => setHeatmapStages(new Set())}
              className="font-mono-tag text-[10px] text-[#78584B] hover:text-[#6B0F1A] hover:underline"
              data-testid="heatmap-clear"
            >
              NONE
            </button>
          </div>
          <ApplicantHeatmap
            applicants={roleFilteredApplicants}
            activeStages={heatmapStages}
            height="560px"
          />
        </>
      ) : (
        <>
      {/* Search + Region + Sort row — every filter now sits on one line
          (region collapsed from a tab strip into a Select) so admins get
          a tight header regardless of viewport. Row wraps on narrow
          screens. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#78584B] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, ZIP, or location…"
            data-testid="applicants-search"
            className="pl-9 bg-[#FBF4E4] border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
          />
        </div>
        {/* Region select — replaces the old horizontal tab strip. Each
            option shows a live per-region count so admins can size a
            region before drilling in. */}
        <div className="inline-flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#78584B]" strokeWidth={2} />
          <span className="font-mono-tag text-[10px] text-[#78584B] tracking-wider hidden sm:inline">REGION</span>
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger
              data-testid="applicants-region-select"
              className="h-9 min-w-[190px] bg-[#FBF4E4] border-[#1A0F0A]/15 rounded-sm text-xs text-[#1A0F0A] font-bold tracking-wide"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabs.map((tab) => (
                <SelectItem
                  key={tab}
                  value={tab}
                  data-testid={`applicants-region-option-${tab.replace(/ /g, "-").toLowerCase()}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span>{tab}</span>
                    <span className="font-mono-tag text-[9px] text-[#78584B]">
                      {perTabCounts[tab] || 0}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="inline-flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-[#78584B]" strokeWidth={2} />
          <span className="font-mono-tag text-[10px] text-[#78584B] tracking-wider hidden sm:inline">SORT</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              data-testid="applicants-sort"
              className="h-9 min-w-[190px] bg-[#FBF4E4] border-[#1A0F0A]/15 rounded-sm text-xs text-[#1A0F0A] font-bold tracking-wide"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`applicants-sort-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Desktop table + Mobile card list */}
      <div className="card-surface rounded-sm overflow-hidden" data-testid="applicants-list">
        {/* Header — desktop only */}
        <div className="hidden md:grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,2fr)_auto] gap-4 px-6 py-4 border-b border-[#1A0F0A]/12 font-mono-tag text-[10px] text-[#78584B]">
          <div>APPLICANT</div>
          <div>STAGE</div>
          <div>CONTACT</div>
          <div>LOCATION</div>
          <div>SCORE</div>
          <div>NOTES</div>
          <div className="text-right pr-1">ACTIONS</div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="no-applicants">
            <UserPlus className="w-8 h-8 mx-auto text-[#1A0F0A]/25 mb-3" strokeWidth={1.5} />
            <p className="text-[#78584B] text-sm">
              {(data?.applicants || []).length === 0
                ? "No applicants yet. Click \"New applicant\" to add the first one."
                : "No applicants match this filter."}
            </p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filtered.map((a) => (
              <ApplicantRow
                key={a.id}
                applicant={a}
                onPatch={patchApplicant}
                onInvite={openOnboardConfirm}
                onEdit={openEdit}
                onDelete={removeApplicant}
                showRoleBadge={roleTab === "all"}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
        </>
      )}

      {/* Create / Edit dialog — rendered globally so "New applicant" works
          across Pipeline, Kanban, and Heatmap views. */}
      <ApplicantEditor
        open={!!editorOpen}
        mode={editorOpen?.mode}
        initial={editorOpen?.value}
        regions={data.tabs}
        onClose={() => setEditorOpen(null)}
        onSave={saveEditor}
      />

      {/* START ONBOARDING confirmation — the single, explicit hand-off
          moment from "internal applicant record" to "real photographer with
          portal access". No email fires until admin confirms here. */}
      <Dialog open={!!onboardConfirm} onOpenChange={(o) => { if (!o && !onboardBusy) setOnboardConfirm(null); }}>
        <DialogContent
          className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A] rounded-sm max-w-md"
          data-testid="onboard-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
              <Send className="w-5 h-5 text-[#6B0F1A]" strokeWidth={2} />
              Start onboarding?
            </DialogTitle>
            <DialogDescription className="text-[#4D3F35]">
              This is the moment the applicant becomes a Reelty photographer.
              Everything below runs the instant you confirm.
            </DialogDescription>
          </DialogHeader>

          {onboardConfirm && (() => {
            // Applicants applying through /apply carry `applied_role`. Legacy
            // rows pre-multi-role fall through to photographer. This drives
            // the dialog copy + the destination role after invite.
            const role = onboardConfirm.applied_role === "pilot" ? "pilot" : "photographer";
            const roleLabel = role === "pilot" ? "pilot" : "photographer";
            const rosterLabel = role === "pilot" ? "pilot roster" : "photographer roster";
            return (
            <div className="space-y-3">
              <div className="card-surface rounded-sm p-3 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-[#1A0F0A]">{onboardConfirm.name}</div>
                  <RoleBadge role={role} />
                </div>
                <div className="text-xs text-[#4D3F35] truncate">{onboardConfirm.email}</div>
                {onboardConfirm.zip_code && (
                  <div className="font-mono-tag text-[10px] text-[#78584B]">
                    ZIP {onboardConfirm.zip_code} · {onboardConfirm.resolved_city || onboardConfirm.region}
                  </div>
                )}
              </div>
              <ul className="space-y-2 text-sm text-[#1A0F0A]">
                <li className="flex items-start gap-2">
                  <UserPlus className="w-4 h-4 text-[#6B0F1A] mt-0.5 shrink-0" strokeWidth={2} />
                  Create a {roleLabel} account with their existing info
                </li>
                <li className="flex items-start gap-2">
                  <MailCheck className="w-4 h-4 text-[#6B0F1A] mt-0.5 shrink-0" strokeWidth={2} />
                  Email them a magic link to start their {roleLabel} onboarding
                </li>
                <li className="flex items-start gap-2">
                  <UserCheck className="w-4 h-4 text-[#6B0F1A] mt-0.5 shrink-0" strokeWidth={2} />
                  Move them off the applicant board and onto the <b>{rosterLabel}</b>
                </li>
              </ul>
              <p className="text-[11px] text-[#78584B] leading-relaxed">
                Until you click below, no email has been sent — the applicant has never touched
                the portal. Once sent, the link is valid for 7 days.
              </p>
            </div>
            );
          })()}

          <DialogFooter className="mt-4 gap-2">
            <Button
              variant="ghost"
              onClick={() => setOnboardConfirm(null)}
              disabled={onboardBusy}
              className="text-[#78584B] hover:bg-[#1A0F0A]/5"
              data-testid="onboard-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmOnboard}
              disabled={onboardBusy}
              data-testid="onboard-confirm-btn"
              className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm btn-primary-glow"
            >
              {onboardBusy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" strokeWidth={2} /> Send magic link</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV IMPORT DIALOG — admin picks a fallback region, drops a CSV,
          we hit /api/admin/applicants/import-csv, then show a summary. */}
      <Dialog open={csvImportOpen} onOpenChange={(o) => { if (!o) { setCsvImportOpen(false); setCsvResult(null); } }}>
        <DialogContent
          className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A] rounded-sm max-w-lg"
          data-testid="csv-import-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Import applicants from CSV</DialogTitle>
            <DialogDescription className="text-[#4D3F35]">
              Accepted columns (any order, case-insensitive): <b>Applicant</b> (or Name),
              Stage, Email, Phone, Zip, Desired Location, Desired Travel Distance,
              Notes, Score, Resume. Rows that share an email with an existing applicant
              will be <b>updated</b> — never duplicated.
            </DialogDescription>
          </DialogHeader>

          {!csvResult ? (
            <>
              <div className="space-y-3">
                <Label className="font-mono-tag text-[10px] text-[#78584B]">FALLBACK REGION</Label>
                <Select value={csvRegion} onValueChange={setCsvRegion}>
                  <SelectTrigger data-testid="csv-region-select" className="bg-white border-[#1A0F0A]/15 rounded-sm text-[#1A0F0A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(data?.tabs || []).map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#78584B]">
                  Applied to rows that don't include a Region column. If your CSV rows have their own Region (e.g. <code>NC</code>, <code>SC</code>, or full state name), those win.
                </p>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  data-testid="csv-file-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setCsvUploading(true);
                    setCsvResult(null);
                    try {
                      const fd = new FormData();
                      fd.append("file", file);
                      const { data: result } = await api.post(
                        `/admin/applicants/import-csv?region=${encodeURIComponent(csvRegion)}`,
                        fd,
                        { headers: { "Content-Type": "multipart/form-data" } },
                      );
                      setCsvResult(result);
                      toast.success(`Imported: ${result.inserted} new, ${result.updated} updated.`);
                      load({ silent: true });
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "CSV import failed.");
                    } finally {
                      setCsvUploading(false);
                      if (csvInputRef.current) csvInputRef.current.value = "";
                    }
                  }}
                />
                <Button
                  onClick={() => csvInputRef.current?.click()}
                  disabled={csvUploading}
                  data-testid="csv-pick-btn"
                  className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm w-full"
                >
                  {csvUploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" strokeWidth={2} /> Choose CSV file…</>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="csv-import-result">
              <div className="rounded-sm border border-[#1A0F0A]/12 bg-white p-4 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-[#4D3F35]">Inserted</span>
                  <span className="font-mono-tag text-[#6B0F1A] font-bold">{csvResult.inserted}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#4D3F35]">Updated</span>
                  <span className="font-mono-tag text-[#6B0F1A] font-bold">{csvResult.updated}</span>
                </div>
                {csvResult.errors?.length > 0 && (
                  <>
                    <div className="pt-2 mt-2 border-t border-[#1A0F0A]/10 text-sm font-bold text-[#6B0F1A]">
                      {csvResult.errors.length} error(s)
                    </div>
                    <ul className="text-xs text-[#4D3F35] max-h-32 overflow-auto">
                      {csvResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => { setCsvResult(null); }}
                  variant="ghost"
                  className="text-[#78584B] hover:bg-[#1A0F0A]/5 flex-1"
                >Import another</Button>
                <Button
                  onClick={() => { setCsvImportOpen(false); setCsvResult(null); }}
                  className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold flex-1"
                  data-testid="csv-import-done"
                >Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
  );
}

function ApplicantRow({ applicant, onPatch, onInvite, onEdit, onDelete, showRoleBadge = true }) {
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState(applicant.notes || "");

  // Save when the value actually changed; also close the editor. Same auto-
  // save-on-blur flow as before — kept because the pipeline row is a dense
  // table where an explicit Save/Cancel per row would be tedious. The roster
  // drawer uses explicit Save/Cancel because it's a single focused card.
  const commitNotes = async () => {
    setNotesEditing(false);
    if (notesDraft === (applicant.notes || "")) return;
    await onPatch(applicant, { notes: notesDraft });
  };

  // Keyboard shortcuts for notes editing so the interaction matches what the
  // roster drawer's Textarea *feels* like:
  //   • Escape → cancel + discard draft
  //   • Cmd/Ctrl + Enter → commit + close
  const onNotesKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setNotesDraft(applicant.notes || "");
      setNotesEditing(false);
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      commitNotes();
    }
  };

  const stageStyle = STAGE_STYLE[applicant.stage] || STAGE_STYLE["Not Contacted"];
  // Applicants and photographers never overlap — the moment we invite, the
  // record is deleted from this collection. `invited` stays around as a
  // constant so the RowActions API doesn't change.
  const invited = false;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      data-testid={`applicant-row-${applicant.id}`}
      className="border-b border-[#1A0F0A]/8 hover:bg-[#F5EBD9]/40 transition-colors"
    >
      {/* Desktop grid */}
      <div className="hidden md:grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,2fr)_auto] gap-4 items-start px-6 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="font-display font-bold text-[#1A0F0A] truncate">{applicant.name || "—"}</div>
            {showRoleBadge && (
              <RoleBadge
                role={applicant.applied_role}
                testId={`applicant-role-badge-${applicant.id}`}
              />
            )}
          </div>
          <div className="font-mono-tag text-[9px] text-[#78584B] mt-1 truncate">
            {applicant.region.toUpperCase()}
          </div>
        </div>

        <div>
          <Select
            value={applicant.stage || "Not Contacted"}
            onValueChange={(v) => onPatch(applicant, { stage: v })}
          >
            <SelectTrigger
              data-testid={`stage-select-${applicant.id}`}
              className={`h-8 px-2.5 text-xs font-bold border rounded-sm ${stageStyle}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="text-xs min-w-0">
          {applicant.email && <div className="text-[#1A0F0A] truncate">{applicant.email}</div>}
          {applicant.phone && <div className="text-[#78584B] mt-0.5">{applicant.phone}</div>}
          {!applicant.email && !applicant.phone && <span className="text-[#78584B]/60">—</span>}
        </div>

        <div className="text-xs min-w-0">
          {applicant.desired_location && (
            <div className="flex items-center gap-1 text-[#1A0F0A] truncate">
              <MapPin className="w-3 h-3 text-[#78584B] shrink-0" strokeWidth={2} />
              <span className="truncate">{applicant.desired_location}</span>
            </div>
          )}
          {applicant.desired_travel_distance && (
            <div className="text-[#78584B] mt-0.5">± {applicant.desired_travel_distance}</div>
          )}
          {applicant.zip_code && (
            <div className="text-[#78584B] mt-0.5 font-mono-tag text-[10px]">{applicant.zip_code}</div>
          )}
        </div>

        <div>
          <ScorePicker
            value={applicant.score || 0}
            onChange={(n) => onPatch(applicant, { score: n })}
            testId={`score-${applicant.id}`}
          />
        </div>

        <div className="min-w-0">
          {notesEditing ? (
            <Textarea
              autoFocus
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={commitNotes}
              onKeyDown={onNotesKeyDown}
              placeholder="Type notes — Enter to newline, ⌘/Ctrl + Enter to save, Esc to cancel"
              className="bg-white border-[#1A0F0A]/15 text-xs min-h-[60px] rounded-sm focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25"
              data-testid={`notes-textarea-${applicant.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => { setNotesDraft(applicant.notes || ""); setNotesEditing(true); }}
              className="text-xs text-[#4D3F35] hover:text-[#6B0F1A] text-left w-full leading-relaxed line-clamp-3 hover:underline decoration-dotted underline-offset-2"
              data-testid={`notes-btn-${applicant.id}`}
              title="Click to edit — saves on blur"
            >
              {applicant.notes || <span className="text-[#78584B]/70 italic">Add a note…</span>}
            </button>
          )}
        </div>

        <RowActions
          applicant={applicant}
          invited={invited}
          inviting={false}
          onInvite={onInvite}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>

      {/* Mobile card */}
      <div className="md:hidden p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <div className="font-display font-bold text-[#1A0F0A] truncate">{applicant.name || "—"}</div>
              {showRoleBadge && (
                <RoleBadge
                  role={applicant.applied_role}
                  testId={`applicant-role-badge-mobile-${applicant.id}`}
                />
              )}
            </div>
            <div className="font-mono-tag text-[9px] text-[#78584B] mt-0.5">
              {applicant.region.toUpperCase()}
            </div>
          </div>
          <Select
            value={applicant.stage || "Not Contacted"}
            onValueChange={(v) => onPatch(applicant, { stage: v })}
          >
            <SelectTrigger
              data-testid={`stage-select-mobile-${applicant.id}`}
              className={`h-8 px-2.5 text-xs font-bold border rounded-sm shrink-0 max-w-[160px] ${stageStyle}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(applicant.email || applicant.phone || applicant.desired_location || applicant.zip_code) && (
          <div className="text-xs space-y-0.5">
            {applicant.email && <div className="text-[#1A0F0A] truncate">{applicant.email}</div>}
            {applicant.phone && <div className="text-[#78584B]">{applicant.phone}</div>}
            {applicant.desired_location && (
              <div className="flex items-center gap-1 text-[#4D3F35]">
                <MapPin className="w-3 h-3 text-[#78584B] shrink-0" strokeWidth={2} />
                <span className="truncate">{applicant.desired_location}</span>
                {applicant.desired_travel_distance && <span className="text-[#78584B]">· ±{applicant.desired_travel_distance}</span>}
              </div>
            )}
            {applicant.zip_code && (
              <div className="text-[#78584B] font-mono-tag text-[10px]">ZIP {applicant.zip_code}</div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <ScorePicker
            value={applicant.score || 0}
            onChange={(n) => onPatch(applicant, { score: n })}
            testId={`score-mobile-${applicant.id}`}
          />
          {safeHref(applicant.resume_url) && (
            <a
              href={safeHref(applicant.resume_url)}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#6B0F1A] hover:underline"
            >
              <FileText className="w-3 h-3" strokeWidth={2} /> Resume <ExternalLink className="w-3 h-3" strokeWidth={2} />
            </a>
          )}
        </div>

        <button
          type="button"
          onClick={() => { setNotesDraft(applicant.notes || ""); setNotesEditing(true); }}
          className="text-xs text-[#4D3F35] hover:text-[#6B0F1A] text-left w-full leading-relaxed hover:underline decoration-dotted underline-offset-2"
          title="Tap to edit — saves on blur"
        >
          {applicant.notes || <span className="text-[#78584B]/70 italic">Add a note…</span>}
        </button>
        {notesEditing && (
          <Textarea
            autoFocus
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={commitNotes}
            onKeyDown={onNotesKeyDown}
            placeholder="Type notes — ⌘/Ctrl + Enter to save, Esc to cancel"
            className="bg-white border-[#1A0F0A]/15 text-xs min-h-[80px] rounded-sm focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25"
          />
        )}

        <RowActions
          applicant={applicant}
          invited={invited}
          inviting={false}
          onInvite={onInvite}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </motion.div>
  );
}

function RowActions({ applicant, invited, inviting, onInvite, onEdit, onDelete }) {
  // "Ready to onboard" hint: stages where clicking the button is the obvious
  // next step. Pure UX — button still works from any stage.
  const readyToOnboard =
    !invited &&
    !!applicant.email &&
    ["Accepted", "Interview Scheduled"].includes(applicant.stage);

  return (
    <div className="flex md:flex-col md:items-end items-center gap-1.5 shrink-0">
      <Button
        size="sm"
        disabled={inviting || !applicant.email || invited}
        onClick={() => onInvite(applicant)}
        data-testid={`onboard-btn-${applicant.id}`}
        title={
          invited
            ? "Photographer account already created — magic link sent."
            : !applicant.email
            ? "Add an email before starting onboarding."
            : "Create a photographer account + send them the magic-link onboarding email."
        }
        className={`font-bold tracking-wide rounded-sm text-[11px] h-8 whitespace-nowrap ${
          invited
            ? "bg-[#1B5E20] hover:bg-[#1B5E20] text-[#F5EBD9] cursor-default"
            : "bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] btn-primary-glow"
        } ${readyToOnboard ? "ring-2 ring-[#B08D57]/60 animate-pulse-slow" : ""}`}
      >
        {inviting ? (
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        ) : invited ? (
          <UserCheck className="w-3 h-3 mr-1" strokeWidth={2} />
        ) : (
          <Send className="w-3 h-3 mr-1" strokeWidth={2} />
        )}
        {invited ? "Onboarding" : "Start Onboarding"}
      </Button>

      {/* Secondary Resume button — every row shows it, but it's disabled when
          no URL is on file so admins immediately see who's missing one. */}
      {safeHref(applicant.resume_url) ? (
        <a
          href={safeHref(applicant.resume_url)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`resume-btn-${applicant.id}`}
          title={`Open resume · ${safeHref(applicant.resume_url)}`}
          className="inline-flex items-center justify-center gap-1 h-7 px-2 rounded-sm border border-[#6B0F1A]/30 bg-transparent text-[10px] font-bold tracking-wide text-[#6B0F1A] hover:bg-[#6B0F1A]/8 whitespace-nowrap"
        >
          <FileText className="w-3 h-3" strokeWidth={2} />
          Resume
          <ExternalLink className="w-2.5 h-2.5 opacity-70" strokeWidth={2} />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => onEdit(applicant)}
          data-testid={`resume-missing-btn-${applicant.id}`}
          title="No resume on file — click to edit and paste the URL."
          className="inline-flex items-center justify-center gap-1 h-7 px-2 rounded-sm border border-dashed border-[#78584B]/40 bg-transparent text-[10px] font-mono-tag tracking-wide text-[#78584B]/80 hover:text-[#6B0F1A] hover:border-[#6B0F1A]/40 whitespace-nowrap"
        >
          <FileText className="w-3 h-3" strokeWidth={2} />
          Add resume
        </button>
      )}

      <div className="flex gap-1">
        <button
          onClick={() => onEdit(applicant)}
          data-testid={`applicant-edit-${applicant.id}`}
          className="p-1.5 text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 rounded-sm"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
        <button
          onClick={() => onDelete(applicant)}
          data-testid={`applicant-delete-${applicant.id}`}
          className="p-1.5 text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8 rounded-sm"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

/**
 * ScorePicker — 5-star rating for the pipeline row. Kept visually identical
 * to the roster drawer's `StarPicker` so the score control feels the same on
 * both sides of the applicant / photographer split. Click the current top
 * star again to clear the score to zero.
 */
function ScorePicker({ value = 0, onChange, testId }) {
  return (
    <div
      className="inline-flex items-center gap-0.5"
      data-testid={testId}
      data-value={value || 0}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          data-testid={`${testId}-star-${n}`}
          title={`Set score to ${n === value ? 0 : n}`}
          className="w-6 h-6 grid place-items-center rounded-sm hover:bg-[#B08D57]/10 cursor-pointer transition-colors"
        >
          <Star
            className={`w-4 h-4 ${
              n <= (value || 0) ? "fill-[#B08D57] text-[#B08D57]" : "text-[#1A0F0A]/20"
            }`}
            strokeWidth={1.75}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Create/edit modal. Same form for both — mode drives the title, button
 * label, and whether we POST or PATCH on save.
 */
function ApplicantEditor({ open, mode, initial, regions, onClose, onSave }) {
  const [draft, setDraft] = useState(initial || emptyForm());
  const [saving, setSaving] = useState(false);

  // Reset draft every time the modal opens with a different initial.
  useEffect(() => {
    if (open) setDraft(initial || emptyForm());
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "edit" ? "Edit applicant" : "New applicant";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[#FBF4E4] border-[#1A0F0A]/12 text-[#1A0F0A] rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="applicant-editor"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight text-[#1A0F0A]">{title}</DialogTitle>
          <DialogDescription className="text-[#4D3F35]">
            {mode === "edit"
              ? "Update this applicant's record. Changes save instantly on click."
              : "Add a new applicant to the pipeline. Only name is required — you can fill in the rest later."}
          </DialogDescription>
        </DialogHeader>

        {mode === "create" && (
          <div
            className="rounded-sm border-l-2 border-[#B08D57] bg-[#F5EBD9] p-3 flex items-start gap-2"
            data-testid="applicant-no-email-hint"
          >
            <Info className="w-4 h-4 text-[#B08D57] mt-0.5 shrink-0" strokeWidth={2} />
            <p className="text-xs text-[#4D3F35] leading-relaxed">
              <b className="text-[#1A0F0A]">Applicants never receive an email at this step.</b>{" "}
              They stay internal to the Reelty team. When they're ready to onboard, hit{" "}
              <b>Start Onboarding</b> on their row — that's when their magic-link email fires.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="applicant-editor-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="NAME *">
              <Input
                required
                value={draft.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="applicant-name-input"
                placeholder="Alex Rivera"
                className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
              />
            </Field>
            <Field label="REGION">
              <Select value={draft.region} onValueChange={(v) => set("region", v)}>
                <SelectTrigger
                  data-testid="applicant-region-select"
                  className="mt-1.5 bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(regions || []).map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="EMAIL">
              <Input
                type="email"
                value={draft.email || ""}
                onChange={(e) => set("email", e.target.value)}
                data-testid="applicant-email-input"
                placeholder="alex@example.com"
                className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
              />
            </Field>
            <Field label="PHONE">
              <Input
                type="tel"
                value={draft.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="applicant-phone-input"
                placeholder="(555) 123-4567"
                className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
              />
            </Field>
            <Field label="ZIP">
              <Input
                value={draft.zip_code || ""}
                onChange={(e) => set("zip_code", e.target.value)}
                data-testid="applicant-zip-input"
                placeholder="28202"
                className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
              />
            </Field>
            <Field label="STAGE">
              <Select value={draft.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger
                  data-testid="applicant-stage-select"
                  className="mt-1.5 bg-white border-[#1A0F0A]/15 text-[#1A0F0A] rounded-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="DESIRED LOCATION">
              <Input
                value={draft.desired_location || ""}
                onChange={(e) => set("desired_location", e.target.value)}
                data-testid="applicant-location-input"
                placeholder="Charlotte, NC"
                className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
              />
            </Field>
            <Field label="TRAVEL DISTANCE">
              <Input
                value={draft.desired_travel_distance || ""}
                onChange={(e) => set("desired_travel_distance", e.target.value)}
                data-testid="applicant-distance-input"
                placeholder="25 mi"
                className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
              />
            </Field>
          </div>

          <Field label="RESUME / PORTFOLIO URL">
            <Input
              type="url"
              value={draft.resume_url || ""}
              onChange={(e) => set("resume_url", e.target.value)}
              data-testid="applicant-resume-input"
              placeholder="https://…"
              className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
            />
          </Field>

          <div>
            <Label className="font-mono-tag text-[10px] text-[#78584B]">SCORE</Label>
            <div className="mt-1.5">
              <ScorePicker
                value={draft.score || 0}
                onChange={(n) => set("score", n)}
                testId="applicant-score-input"
              />
            </div>
          </div>

          <Field label="NOTES">
            <Textarea
              value={draft.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="applicant-notes-input"
              placeholder="Anything the team should know…"
              rows={3}
              className="mt-1.5 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A]"
            />
          </Field>

          <DialogFooter className="mt-6 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-[#78584B] hover:bg-[#1A0F0A]/5"
              data-testid="applicant-cancel-btn"
            >
              <X className="w-4 h-4 mr-1.5" strokeWidth={2} />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="applicant-save-btn"
              className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm"
            >
              {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Add applicant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-mono-tag text-[10px] text-[#78584B]">{label}</Label>
      {children}
    </div>
  );
}
